import { ARBleCampaign, ARBleOrder, ARBleOrderItem } from '../../../../models/agrorendement-ble.model';
import { KeycloakService } from '../../../../services/keycloak.service';
import { correctDate, isEmpty } from '../../../../utils/utils';

export class ARBleUtils {
  public static isOrderEditable(order: ARBleOrder): boolean {
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    const today = correctDate(new Date());
    const campaign = order.campaign;
    const config = campaign.configuration;

    // si non admin => false
    if (!isAdmin) {
      return false;
    }

    // si fin Commande T1 pas définie => OK
    if (config.orderT1EndingOn === null) {
      return true;
    }
    // si commande créée avant fin CommandeT1 :
    // test si today est avant OrderT1EndingOn
    // sinon la commande nep eut être modifiable
    if (order.createdAt <= config.orderT1EndingOn) {
      return today <= config.orderT1EndingOn;
    }

    // si fin Commande T2 pas définie => OK
    if (config.orderT2EndingOn === null) {
      return true;
    }
    // si commande créée avant fin CommandeT2 :
    // test si today est avant OrderT2EndingOn
    // sinon la commande nep eut être modifiable
    if (order.createdAt <= config.orderT2EndingOn) {
      return today <= config.orderT2EndingOn;
    }
    // commande créée après fin Commande T2
    if (config.orderT3EndingOn === null) {
      return true;
    }
    // fin Commande T3 existe : on doit être avant fin Commande T3
    return today <= config.orderT3EndingOn;
  }

  public static isCampaignCurrentlyOpen(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      (config.dataT3EndingOn === null || config.dataT3EndingOn === undefined || config.dataT3EndingOn >= today)
    );
  }

  public static isT1DataCurrentlyOpen(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      (config.dataT1EndingOn === null || config.dataT1EndingOn === undefined || config.dataT1EndingOn >= today)
    );
  }

  public static isT1OrderCurrentlyOpen(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      (config.orderT1EndingOn === null || config.orderT1EndingOn === undefined || config.orderT1EndingOn >= today)
    );
  }

  public static isT2DataCurrentlyOpen(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      (config.dataT2EndingOn === null || config.dataT2EndingOn === undefined || config.dataT2EndingOn >= today)
    );
  }

  public static isT2OrderCurrentlyOpen(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      (config.orderT2EndingOn === null || config.orderT2EndingOn === undefined || config.orderT2EndingOn >= today)
    );
  }

  public static isT3DataHasStarted(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      config.dataT3StartingOn !== null &&
      config.dataT3StartingOn !== undefined &&
      config.dataT3StartingOn <= today
    );
  }

  public static isT3DataCurrentlyOpen(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      config.dataT3StartingOn !== null &&
      config.dataT3StartingOn !== undefined &&
      config.dataT3StartingOn <= today &&
      (config.dataT3EndingOn === null || config.dataT3EndingOn === undefined || config.dataT3EndingOn >= today)
    );
  }

  public static isT3OrderCurrentlyOpen(campaign: ARBleCampaign): boolean {
    const today = correctDate(new Date());
    const config = campaign.configuration;
    return (
      config.startingOn <= today &&
      (config.orderT3EndingOn === null || config.orderT3EndingOn === undefined || config.orderT3EndingOn >= today)
    );
  }

  public static canBeOrdered(
    orderItem: ARBleOrderItem,
    hasOpenedCampaign: boolean,
    isT1OrderCurrentlyOpen: boolean,
    isT2OrderCurrentlyOpen: boolean,
    isT3OrderCurrentlyOpen: boolean,
    isT3DataHasStarted: boolean,
    isT3DataCurrentlyOpen: boolean,
    productTypesIndex: Map<number, string>,
    frequenciesIndex: Map<number, string>,
    consoleModulationTypesIndex: Map<number, string>,
  ): boolean {
    // TODO implémenter ces conditions sur le serveur
    // pour 2N : doit avoir T1, T2 et T3 + doivent être commandables donc pas de condition en plus
    return (
      orderItem &&
      !orderItem.order &&
      hasOpenedCampaign &&
      ((orderItem.hasT1 && isT1OrderCurrentlyOpen) ||
        (!(!isT1OrderCurrentlyOpen && orderItem.hasT1) && orderItem.hasT2 && isT2OrderCurrentlyOpen) ||
        // quand a T3 => peut cocher la case et commander
        // avant le début des données T3 => pas de formulaire
        // après le début des données T3 => formulaire doit être correct
        // sauf pour t3azotealreadyspread
        // dans ce cas => soit T2 est ouvert ou bien T2 non commandé
        (!(!isT2OrderCurrentlyOpen && orderItem.hasT2) &&
          orderItem.hasT3 &&
          isT3OrderCurrentlyOpen &&
          (!isT3DataHasStarted ||
            (isT3DataCurrentlyOpen &&
              ARBleUtils.isT3VraValid(orderItem, consoleModulationTypesIndex) &&
              ARBleUtils.isT3FertilizerValid(orderItem) &&
              ARBleUtils.isAgrofertValid(orderItem)))))
    );
  }

  public static isT3VraValid(orderItem: ARBleOrderItem, consoleModulationTypesIndex: Map<number, string>): boolean {
    if (!orderItem.t3ConsoleModulationType || !orderItem.t3ConsoleModulationType.id) {
      return false;
    }
    const consoleModulationType = consoleModulationTypesIndex.get(+orderItem.t3ConsoleModulationType.id);
    return (
      consoleModulationType === 'NONE' ||
      (!!orderItem.t3ConsoleFormatType &&
        !!orderItem.t3ConsoleFormatType.id &&
        !!orderItem.t3ConsoleVersion &&
        !!orderItem.t3ConsoleVersion.id &&
        !!orderItem.t3ConsoleType &&
        !!orderItem.t3ConsoleType.id)
    );
  }

  public static isT3FertilizerValid(orderItem: ARBleOrderItem) {
    return (
      orderItem.t3FertilizerType && orderItem.t3FertilizerType.id !== null && !!orderItem.t3FertilizerConcentration
    );
  }

  public static isAgrofertValid(orderItem: ARBleOrderItem) {
    // pas besoin d'agrofert => azote donné par l'utilisateur
    const validated: boolean = !isEmpty(orderItem.objective) && !isEmpty(orderItem.userProvidedAzote);
    return validated;
  }
}
